import { ChevronDownIcon, XCircleFillIcon } from '@primer/octicons-react';
import { useEffect, useMemo, useState } from 'react';
import { useCommonContext } from '../../contexts/common';
import { apiClient } from '../../api/apiClient';
import { NotifyHelper } from '../../helpers/toast';

import { Box, Button, SelectPanel } from '@primer/react';
import { VariantType } from '@primer/react/lib/Button/types';
import Text from '../ui/text';
import DieuPhoiVienResponse from '../../models/response/giao-vien/DieuPhoiVienResponse';

type IComboBoxDieuPhoiVienProps = {
	role_dpv_id?: number;
	dm_coso_id?: number;
	isReadonly?: boolean;
	value: number;
	onValueChanged: (id: number, data?: DieuPhoiVienResponse) => void;
	className?: string;
	isShowClearButton?: boolean;
	preText?: string;
	width?: string | number;
	stylingMode?: 'outlined' | 'filled' | 'underlined';
	isShowClearBtn?: boolean,
	maxWidth?: any,
	variant?: VariantType
};

const getTrailingVisual = (dieuPhoiVien: DieuPhoiVienResponse) => {
	return (
		<Text text={dieuPhoiVien.email || ''} sx={{
			color: "fg.muted",
			fontSize: "12px"
		}} />
	);
}

const ComboBoxDieuPhoiVien = (props: IComboBoxDieuPhoiVienProps) => {
	const { translate } = useCommonContext();
	const [dieuPhoiVien, setDieuPhoiVien] = useState<DieuPhoiVienResponse[]>([]);
	const [loading, setLoading] = useState(false);
	const [open, setOpen] = useState(false);
	const [filter, setFilter] = useState('');

	useEffect(() => {
		const fetchData = async () => {
			setLoading(true);
			try {
				const response = await apiClient.get('giao-vien/dieu-phoi-vien?role_dpv_id=' + props.role_dpv_id + '&dm_coso_id=' + props.dm_coso_id);
				if (response && response.is_success && response.data) {
					setDieuPhoiVien(response.data);
				} else {
					NotifyHelper.Error(translate("Error.LoadDataFailed"));
				}
			} catch (error) {
				console.error("Error fetching điều phối viên data:", error);
				NotifyHelper.Error(translate("Error.LoadDataFailed"));
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, [translate]);

	const dataSource = useMemo(() => {
		return dieuPhoiVien
			.filter(x => x.full_name && x.full_name !== '0' && x.full_name.trim() !== '')
			.map(x => {
				const item: any = {
					id: x.id,
					text: x.full_name,
					search: `${x.full_name} ${x.email} ${x.username || ''}`,
					trailingVisual: getTrailingVisual(x),
					data: x
				}
				return item;
			})
	}, [dieuPhoiVien]);

	const filterdData = useMemo(() => {
		return dataSource.filter(item =>
			item.search.toLowerCase().includes(filter.toLowerCase())
		)
	}, [dataSource, filter]);

	const _selectedData = useMemo(() => {
		return dataSource.find(item => item.id === props.value)
	}, [props.value, dataSource]);

	if (loading) {
		return (
			<Button
				variant={props.variant}
				sx={{ width: '100%' }}
				disabled
			>
				{translate("Loading...")}
			</Button>
		);
	}

	return (
		<SelectPanel
			renderAnchor={({ children, 'aria-labelledby': ariaLabelledBy, ...anchorProps }) => (
				<Button
					variant={props.variant}
					sx={{
						width: '100%',
					}}
					className="button-left"
					trailingAction={ChevronDownIcon}
					aria-labelledby={` ${ariaLabelledBy}`}
					{...anchorProps}
				>
					<p style={{ maxWidth: props.maxWidth, overflow: "hidden", textOverflow: "ellipsis" }}>
						{children || 'Chọn điều phối viên'}
					</p>
				</Button>
			)}
			title={
				<Box sx={{ display: "flex" }}>
					<Box sx={{ flex: 1 }}>
						{translate(`ComboBoxGiaoVien.PlaceHolder`)}
					</Box>
					{(props.isShowClearBtn === undefined || props.isShowClearBtn === true) && props.value > 0 &&
						<Button
							trailingVisual={XCircleFillIcon}
							variant='invisible'
							sx={{
								color: "danger.emphasis"
							}}
							onClick={() => {
								props.onValueChanged(0)
							}}
						>
							Bỏ chọn
						</Button>
					}
				</Box>
			}
			placeholderText="Search"
			open={open}
			onOpenChange={setOpen}
			items={filterdData}
			selected={_selectedData}
			onSelectedChange={(data: any) => {
				props.onValueChanged(data.id, data.data)
			}}
			onFilterChange={setFilter}
			showItemDividers={true}
			overlayProps={{ width: 'large', height: 'medium' }}
		/>
	);
};

export default ComboBoxDieuPhoiVien;
